import React, { useState, useRef, useMemo, useEffect } from "react";
import { useBonus } from "../context/BonusContext";
import { useStatic } from "../context/StaticContext";
import { useTranslation } from "../hooks/useTranslation";
import { getUserIdFromUrl } from "../helper/apiHelper";
import { FiGift, FiTarget, FiStar, FiCommand } from "react-icons/fi";
import { BiCategoryAlt, BiFootball, BiTestTube } from "react-icons/bi";
import { MdOutlineAttachMoney } from "react-icons/md";
import { RiGamepadLine, RiCoinLine } from "react-icons/ri";
import { IoTrophyOutline } from "react-icons/io5";
import { HiOutlineShoppingBag } from "react-icons/hi";
import { GiPokerHand } from "react-icons/gi";
// Badge component for showing item counts

const Badge = ({ count }) => {
  if (!count || count <= 0) return null;

  return (
    <div
      style={{
        position: "absolute",
        top: "0px",
        right: "5px",
        backgroundColor: "#ff4757",
        color: "white",
        borderRadius: "12px",
        padding: "0 6px",
        fontSize: "11px",
        minWidth: "16px",
        height: "16px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontWeight: "bold",
        zIndex: 10,
        boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
        transform: "translate(50%, -50%)",
      }}
    >
      {count}
    </div>
  );
};

const SubCategoryMenu = ({
  tabsContainerRef,
  formattedTabs,
  activeTabs,
  getSubCategoryIcon,
  handleTabChange,
}) => {
  return (
    <div className="sub-category-menu" style={{ overflow: "visible" }}>
      <div
        className="tabs-container"
        ref={tabsContainerRef}
        style={{ overflow: "visible" }}
      >
        {formattedTabs.length > 0 ? (
          formattedTabs.map((tab) => (
            <button
              key={tab.id}
              className={`tab-button ${activeTabs.includes(tab.id) ? "active" : ""}`}
              onClick={() => handleTabChange(tab.id)}
              style={{
                backgroundColor: activeTabs.includes(tab.id)
                  ? "var(--tab-active-bg-color)"
                  : "var(--tab-bg-color)",
                color: "var(--tab-text-color)",
                boxShadow: activeTabs.includes(tab.id)
                  ? `0 6px 12px var(--tab-active-glow-color)`
                  : "0 4px 8px rgba(0, 0, 0, 0.15)",
                position: "relative",
                overflow: "visible",
              }}
            >
              <span className="tab-icon" style={{ color: "var(--tab-icon-color)" }}>
                {getSubCategoryIcon(tab.id)}
              </span>
              <span className="tab-label">{tab.label}</span>
              <Badge count={tab.count} />
            </button>
          ))
        ) : (
          <div className="tabs-loading">Yükleniyor...</div>
        )}
      </div>
    </div>
  );
};

const TabMenu = () => {
  const { tabs, bonusData, gamesData, loading, gamesLoading } = useBonus();
  const { buttonURLs, activeTabs, setActiveTabs, activeMainCategory, setActiveMainCategory } = useStatic();
  const { tTab } = useTranslation();
  const tabsContainerRef = useRef(null);
  const [redirecting, setRedirecting] = useState(null); // 'cekilis' veya 'puanmarket'
  
  // /lobby/<userid> gibi bir URL ile gelinirse direkt oyunlar sekmesini aç
  useEffect(() => {
    try {
      const path = window.location.pathname.toLowerCase();
      if (path.includes('/lobby/')) {
        setActiveMainCategory('oyunlar');
        setActiveTabs(['tumu']);
      }
    } catch (_) {}
  }, []);

  // Lobby modunu tespit et (bonuslar sekmesini gizlemek için)
  const isLobbyMode = useMemo(() => {
    try {
      return window.location.pathname.toLowerCase().includes('/lobby/');
    } catch (_) {
      return false;
    }
  }, []);
  
  // Tüm veriler yüklenene kadar bekle
  const allDataLoaded = !loading && !gamesLoading;
  // Sabit ana kategoriler (Figma tasarımındaki gibi)
  const { isMainCategoriesActive, mainCategories } = useMemo(() => {
    const baseCategories = [];

    // Lobby modunda değilsek bonuslar kategorisini göster
    if (!isLobbyMode) {
      baseCategories.push({
        id: "bonuslar",
        label: tTab("bonuslar", "Bonuslar"),
        icon: <FiGift size={20} />,
      });
    }

    // Oyunlar kategorisini veriler yüklendiyse ve (oyun verisi varsa veya opsiyonel URL gelmişse) ekle
    const hasGamesURL = buttonURLs?.gamesURL && buttonURLs.gamesURL.trim() !== '';
    if (allDataLoaded && ((gamesData && gamesData.length > 0) || hasGamesURL)) {
      baseCategories.push({
        id: "oyunlar",
        label: tTab("oyunlar", "Çark"),
        icon: <RiGamepadLine size={20} />,
        buttonURL: hasGamesURL ? buttonURLs.gamesURL : undefined,
      });
    }

        // Ana kategori menüsünü aktif et eğer:
    // 1. Tüm veriler yüklendi VE (Oyunlar verisi varsa VEYA Çekiliş/puan market URL'leri varsa)
    const hasExternalUrls = 
      (buttonURLs?.lotteryURL && buttonURLs.lotteryURL.trim() !== '') ||
      (buttonURLs?.pointShopURL && buttonURLs.pointShopURL.trim() !== '') ||
      (buttonURLs?.gamesURL && buttonURLs.gamesURL.trim() !== '');
    
    const shouldShowMainCategories = allDataLoaded && (
      (gamesData && gamesData.length > 0) || hasExternalUrls
    );

    let allCategories = [...baseCategories];

    // Çekiliş URL'i varsa ekle
    if (buttonURLs?.lotteryURL && buttonURLs.lotteryURL.trim() !== '') {
      console.log('Çekiliş URL ekleniyor:', buttonURLs.lotteryURL);
      allCategories.push({
        id: "cekilis",
        label: tTab("cekilis", "Çekiliş"),
        icon: <IoTrophyOutline size={20} />,
        buttonURL: buttonURLs.lotteryURL,
      });
    }

    // Puan market URL'i varsa ekle
    if (buttonURLs?.pointShopURL && buttonURLs.pointShopURL.trim() !== '') {
      console.log('Puan Market URL ekleniyor:', buttonURLs.pointShopURL);
      allCategories.push({
        id: "puanmarket",
        label: tTab("puanmarket", "Puan Market"),
        icon: <HiOutlineShoppingBag size={20} />,
        buttonURL: buttonURLs.pointShopURL,
      });
    }

    return {
      isMainCategoriesActive: shouldShowMainCategories,
      mainCategories: allCategories,
    };
  }, [buttonURLs, gamesData, tTab, allDataLoaded, isLobbyMode]);

  // Alt kategoriler için icon eşleştirmeleri
  const getSubCategoryIcon = (tabId) => {
    const iconMap = {
      tumu: <BiCategoryAlt size={22} />,
      yatirim: <MdOutlineAttachMoney size={22} />,
      kayip: <FiTarget size={22} />,
      hosgeldin: <FiStar size={22} />,
      spor: <BiFootball size={22} />,
      casino: <GiPokerHand size={22} />,
      deneme: <BiTestTube size={22} />,
      haftalik: <RiCoinLine size={22} />,
      default: <FiCommand size={22} />,
    };
    return iconMap[tabId] || iconMap.default;
  };

  // API'den gelen sekme listesini işleyip kullanıma hazır hale getir
  const formattedTabs = useMemo(() => {
    // Tüm veriler yüklenmemişse boş array döndür
    if (!allDataLoaded) {
      return [];
    }

    // Aktif ana kategoriye göre uygun veriyi seç
    const currentData = activeMainCategory === "oyunlar" ? gamesData : bonusData;
    
    // Oyunlar için sadece "Tümü" butonunu göster ve rozet olarak toplam oyun sayısını yaz
    if (activeMainCategory === "oyunlar") {
      return [
        {
          id: "tumu",
          label: tTab("tumu", "Tümü"),
          count: Array.isArray(gamesData) ? gamesData.length : 0,
        },
      ];
    }
    
    return tabs.map((tab) => {
      // Tab ID'leri küçük harfe çevir ve Türkçe karakterleri kaldır
      const id = tab
        .toLowerCase()
        .replace(/ü/g, "u")
        .replace(/ı/g, "i")
        .replace(/ö/g, "o")
        .replace(/ğ/g, "g")
        .replace(/ş/g, "s")
        .replace(/ç/g, "c")
        .replace(/\s+/g, ""); // Boşlukları kaldır

      return {
        id,
        label: tTab(id, tab),
        count: currentData.filter((item) => item.bonus_type.includes(tab)).length,
      };
    });
  }, [bonusData, gamesData, tabs, activeMainCategory, allDataLoaded]);

  const handleTabChange = (tabId) => {
    if (tabId === "tumu") {
      setActiveTabs(["tumu"]);
      return;
    }

    // Aynı tab yeniden seçilirse işlem yapma
    if (activeTabs.length === 1 && activeTabs[0] === tabId) {
      return;
    }

    // Her zaman tek bir kategori aktif olsun
    setActiveTabs([tabId]);
  };

  const handleMainTabChange = (tabId, buttonURL) => {
    // Eğer bu ana kategori için bir URL tanımlıysa (opsiyonel), direkt yönlendir
    if (buttonURL && typeof buttonURL === 'string' && buttonURL.trim() !== '') {
      // Eğer zaten yönlendirme yapılıyorsa, tekrar işlem yapma
      if (redirecting) {
        console.log('Zaten yönlendirme yapılıyor, işlem iptal edildi');
        return;
      }
      
      // Loading state'i aktif et
      setRedirecting(tabId);
      
      try {
        // URL'i düzgün formatla (protocol ekle eğer yoksa)
        let formattedURL = buttonURL.trim();
        
        // Boş URL kontrolü
        if (!formattedURL) {
          console.error('Boş URL tespit edildi:', buttonURL);
          setRedirecting(null);
          return;
        }
        
        // Protocol kontrolü yap
        if (!formattedURL.startsWith('http://') && !formattedURL.startsWith('https://')) {
          // Eğer protocol yoksa https:// ekle
          formattedURL = 'https://' + formattedURL;
        }
        
        // Eğer URL içerisinde 'userid' placeholder'ı varsa, gerçek userId ile değiştir
        const userId = getUserIdFromUrl();
        if (typeof formattedURL === 'string' && formattedURL.toLowerCase().includes('userid')) {
          const safeUserId = (userId || '').trim();
          formattedURL = formattedURL.replace(/userid/gi, safeUserId);
        }
        
        console.log(`${tabId} yönlendirmesi yapılıyor:`, formattedURL);
        
        // Yönlendirmeyi hemen yap
        setTimeout(() => {
          try {
            // Önce geçerli URL kontrolü yap
            new URL(formattedURL); // URL geçerli mi test et
            window.location.assign(formattedURL);
          } catch (error) {
            console.error('URL geçersiz veya assign hatası:', error);
            // Fallback olarak href kullan
            try {
              window.location.href = formattedURL;
            } catch (hrefError) {
              console.error('href ile yönlendirme de başarısız:', hrefError);
              setRedirecting(null);
            }
          }
        }, 50); // Daha kısa delay
        
        // 5 saniye sonra loading state'i temizle (timeout için)
        setTimeout(() => {
          setRedirecting(null);
        }, 5000);
        
        return;
      } catch (error) {
        console.error(`${tabId} yönlendirme hatası:`, error);
        setRedirecting(null);
        
        // Hata durumunda basit yönlendirme dene
        try {
          setTimeout(() => {
            window.open(buttonURL, '_self');
          }, 100);
        } catch (fallbackError) {
          console.error('Fallback yönlendirme de başarısız:', fallbackError);
        }
        return;
      }
    }
    
    setActiveMainCategory(tabId);
    // Ana kategori değiştiğinde tab'ları tümü'ye sıfırla
    setActiveTabs(["tumu"]);
  };

  // Tüm veriler yüklenmemişse loading göster
  if (!allDataLoaded) {
    return (
      <div className="navigation-wrapper" style={{ overflow: "visible" }}>
        <div className="tabs-loading">Kategoriler yükleniyor...</div>
      </div>
    );
  }

  return (
    <div className="navigation-wrapper" style={{ overflow: "visible" }}>
      {/* Ana kategori sekmesi */}
      <div className="main-category-menu">
        {isMainCategoriesActive ? (
          mainCategories.map((category) => (
            <button
              key={category.id}
              className={`main-category-button ${
                activeMainCategory === category.id ? "active" : ""
              } ${redirecting === category.id ? "redirecting" : ""}`}
              onClick={() => {
                handleMainTabChange(category.id, category.buttonURL);
              }}
              disabled={redirecting === category.id}
              style={{
                backgroundColor:
                  activeMainCategory === category.id
                    ? "var(--main-category-active-bg-color)"
                    : "var(--main-category-bg-color)",
                color: "var(--main-category-text-color)",
                boxShadow:
                  activeMainCategory === category.id
                    ? `0 6px 12px var(--main-category-active-glow-color)`
                    : "0 4px 8px rgba(0, 0, 0, 0.2)",
                opacity: redirecting === category.id ? 0.7 : 1,
                cursor: redirecting === category.id ? 'not-allowed' : 'pointer',
              }}
            >
              <span
                className="main-category-icon"
                style={{ color: "var(--main-category-icon-color)" }}
              >
                {redirecting === category.id ? (
                  <div 
                    style={{
                      width: '20px',
                      height: '20px',
                      border: '2px solid var(--main-category-icon-color)',
                      borderTop: '2px solid transparent',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite'
                    }}
                  />
                ) : (
                  category.icon
                )}
              </span>
              <span className="main-category-label">
                {redirecting === category.id ? 'Yönlendiriliyor...' : category.label}
              </span>
            </button>
          ))
        ) : (
          <SubCategoryMenu
            tabsContainerRef={tabsContainerRef}
            formattedTabs={formattedTabs}
            activeTabs={activeTabs}
            handleTabChange={handleTabChange}
            getSubCategoryIcon={getSubCategoryIcon}
          />
        )}
      </div>

      {/* Ayırıcı çizgi her zaman gösterilsin */}
      <div className="divider"></div>

      {/* Alt kategori sekmesi (oyunlar seçiliyken gizle) */}
      {isMainCategoriesActive ? (
        <SubCategoryMenu
          tabsContainerRef={tabsContainerRef}
          formattedTabs={formattedTabs}
          activeTabs={activeTabs}
          handleTabChange={handleTabChange}
          getSubCategoryIcon={getSubCategoryIcon}
        />
      ) : null}
    </div>
  );
};

export default TabMenu;
